// ─────────────────────────────────────────────────────────────────────────────
// PHONE NUMBERS, FORMATTED ONCE.
//
// 🚨 STORED DIGITS WERE BEING RENDERED RAW. A partner's phone is kept as ten
// digits — 6674210431 — and the admin Overview printed the column, so staff read
// a number they had to parse by eye and could not tap.
//
// ⚠️ TWO FUNCTIONS, BECAUSE THERE ARE TWO JOBS. An INPUT formatter has to accept a
// half-typed number and return something sensible at every keystroke; a DISPLAY
// formatter is handed a finished value and must never mangle one it does not
// recognise. Two near-identical copies of the input one already existed in
// DoorlyProRegister and RequestShowingModal, which is why this file exists.
// ─────────────────────────────────────────────────────────────────────────────

const digits = (v) => String(v || '').replace(/\D/g, '');

/**
 * As-you-type formatting for an input. Truncates at ten digits, because that is
 * what the field accepts.
 */
export function formatPhone(v) {
  const d = digits(v).slice(0, 10);
  if (!d) return '';
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

export const isValidPhone = (v) => digits(v).length === 10;

/**
 * Display formatting for a stored value.
 *
 * ⚠️ IT RETURNS THE ORIGINAL WHEN IT DOES NOT RECOGNISE THE SHAPE, rather than
 * truncating to ten digits like the input formatter. A stored number may carry a
 * country code or an extension, and a display helper that silently drops digits
 * turns a reachable number into an unreachable one that LOOKS right — which is
 * worse than the unformatted string it replaced.
 */
export function displayPhone(v) {
  const raw = String(v || '').trim();
  if (!raw) return '';
  const d = digits(raw);
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length === 11 && d[0] === '1') return `+1 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  return raw;
}

/**
 * A tel: target, or null when there is nothing dialable.
 *
 * 🔑 DIGITS ONLY, AND THE ORIGINAL SEPARATORS ARE DROPPED. A tel: href containing
 * spaces or parentheses is handled inconsistently across dialers; the visible
 * text stays formatted and the href is what the device actually calls.
 */
export function telHref(v) {
  const d = digits(v);
  if (d.length < 7) return null;
  return `tel:${d.length === 11 && d[0] === '1' ? `+${d}` : d}`;
}

// ── 🔑 THE APPLICATION'S PHONE FIELDS (Jesse, 2026-10-19, phone pass item 8) ─────────────────────────────────────
// Typed as (XXX) XXX-XXXX with the numeric keypad, US 10 digits only, stored as the 10 digits (the backend's
// convention: digits only, compared on the last 10 — there is no E.164 helper and no SMS provider to match). A
// leading US "1" pasted in front of ten digits is dropped. The server applies the same rule (backend/constants/phone.js).
export const PHONE_ERROR = 'Enter a 10-digit phone number';

/** The digits to store: at most 10, a leading US "1" dropped from an 11-digit entry. */
export function phoneDigits(v) {
  let d = digits(v);
  if (d.length === 11 && d[0] === '1') d = d.slice(1);
  return d.slice(0, 10);
}

/** Props for an <input> that types a phone number: numeric keypad, the mask shown, the digits handed back. */
export const phoneInputProps = (value, onDigits) => ({
  type: 'tel', inputMode: 'numeric', autoComplete: 'tel-national', maxLength: 14,
  placeholder: '(555) 123-4567', value: formatPhone(phoneDigits(value)),
  onChange: (e) => onDigits(phoneDigits(e.target.value)),
});
